// Canvas single sink (TASK-493, locked architecture). The one place every summon
// source writes to: the dev button now; the radar Swap/Email buttons (TASK-498)
// and the agent WIDGET chunk (TASK-500) later. Append-only; chrome flags mutate
// in place. Ported from SixEase AppShell's lifted state into a store (3 sources, 1 sink).
import { create } from 'zustand';
import type { CanvasTileSpec, WidgetSpec } from '../registry/types';
import { resolveSize } from '../registry/tileLayout';
import { FILTER_WHITELIST } from '../registry/filterContract';
import { URL_SAFE_PROPS } from './canvasUrl';
import type { RequestedPrompt } from './createChatStore';

/**
 * TASK-790: the one dimension the CanvasDrawer and the dock's TranscriptSheet
 * must agree on, in one place.
 *
 * TASK-768 fixed the drawer clipping the transcript by making the sheet step
 * aside to the left of it — but the drawer's width lived in `CanvasDrawer.tsx`
 * and the sheet's offset in `TranscriptSheet.tsx`, as two independent literals
 * that happened to describe the same 42rem. Widen the drawer and the sheet
 * silently overlaps again, with no test failing.
 *
 * Both stay literal strings because Tailwind's scanner cannot see through
 * interpolation — an `xl:right-[${n}rem]` would simply never be generated. What
 * this buys is that the two literals are adjacent and commented, so the pair
 * cannot be changed one at a time.
 */
export const CANVAS_DRAWER_GEOMETRY = {
  /** The drawer's own width. */
  width: 'max-w-2xl',
  /** The sheet's right offset where both fit side by side — equals `width`. */
  stepAside: 'xl:right-[42rem]',
} as const;

// Session-stable tile ids — pin/close/collapse key off identity, not array index.
let tileSeq = 0;
const stampTiles = (specs: WidgetSpec[]): CanvasTileSpec[] =>
  specs.map((s) => ({
    ...s,
    id: `tile-${tileSeq++}`,
    size: resolveSize(s),
    collapsed: false,
    addedAt: Date.now(),
  }));

// TASK-651: a generated view's SEMANTIC IDENTITY — the same component for the
// same client/subject is the same view. Repeating a request UPDATES that view
// in place instead of appending a duplicate. Conversation bubbles are never
// deduplicated.
//
// TASK-707 widened it to include the whitelisted FILTERS. Identity was
// component + client + subject, so "<client>'s last meeting" and "<client>'s
// upcoming meetings" collapsed onto one tile: the RM's rephrased request
// silently re-updated the previous view and the UI reported that non-escape as
// a feature ("↻ … not duplicated"). Filters ARE part of what was asked for.
//
// Only whitelisted filters count. Including every prop would make each tile
// unique and reintroduce the duplicate-tile problem TASK-651 fixed; ignoring
// them makes a different question look like the same one.
const filterIdentity = (spec: WidgetSpec): string => {
  const allowed = FILTER_WHITELIST[spec.component];
  if (!allowed || !spec.props) return '';
  return Object.keys(allowed)
    .sort() // key order must not change identity
    .filter((k) => spec.props?.[k] !== undefined)
    .map((k) => {
      const v = spec.props?.[k];
      // Arrays are order-insensitive: status ['live','completed'] is the same
      // request as ['completed','live'].
      return `${k}=${Array.isArray(v) ? [...v].map(String).sort().join(',') : String(v)}`;
    })
    .join('&');
};

// TASK-726/729: identity gains the SIGNAL SUBJECT.
//
// `ChangeRadar` summoned `{ clientId }` alone, so opening the CIO signal and then the DNA
// signal for the SAME client updated the first tile in place — the RM saw one view and
// believed it was about the signal they had just clicked. Two subjects on one client are two
// investigations and must be two tiles.
//
// Client-SUMMARY widgets carry no `subjectKey` and therefore keep deduping per client
// exactly as TASK-651 intended: adding the dimension unconditionally would make every tile
// unique again and reintroduce the duplicate-tile problem that fixed.
//
// TASK-733: `reviewId` joins for the same reason `subjectKey` did — two KYC reviews for one
// client are two matters and must be two tiles, while re-opening the SAME review focuses
// the existing tile. Widgets without the prop are byte-for-byte unaffected.
//
// TASK-742: `documentId` joins for ArtifactCard — two generated documents are two cards,
// while re-generating the SAME document updates its card in place. No other spec carries
// the prop today, so widgets without it are byte-for-byte unaffected.
export const tileIdentity = (spec: WidgetSpec): string =>
  `${spec.component}::${String(spec.props?.clientId ?? '')}::${String(
    spec.props?.subject ?? ''
  )}::${String(spec.props?.subjectKey ?? '')}::${String(
    spec.props?.reviewId ?? ''
  )}::${String(spec.props?.documentId ?? '')}::${filterIdentity(spec)}`;

export type SpecAction = { component: string; action: 'added' | 'updated' };

/** Pure preview of what addSpecs would do — lets the caller phrase its reply
 * ("updated the existing view") BEFORE mutating the canvas. */
export const previewActions = (
  current: CanvasTileSpec[],
  incoming: WidgetSpec[],
): SpecAction[] =>
  incoming
    .filter((s) => s.component !== 'ChatMessage')
    .map((s) => ({
      component: s.component,
      action: current.some((t) => !t.rail && tileIdentity(t) === tileIdentity(s))
        ? ('updated' as const)
        : ('added' as const),
    }));

// TASK-651 named workstreams — persisted as WIDGET SPECS ONLY (component + props
// + size). Specs are pointers (ids/filters); widgets self-fetch on restore, so
// no client content ever sits in browser storage. Conversation bubbles are
// session-only and deliberately not persisted.
//
// TASK-876: they are persisted SERVER-SIDE now. The specs-only rule is unchanged and is
// what makes that safe — moving this to the server moves ids and filters, not holdings.
// What changed is who can see it: a workstream saved on this device was invisible to the
// colleague it was saved for, which is the opposite of what saving one is for.
export interface SavedWorkstream {
  name: string;
  savedAt: string;
  clientName?: string;
  specs: WidgetSpec[];
  /** Server id. Absent for an entry still only in this browser's legacy store. */
  id?: string;
  /** Who saved it — attribution, shown so a shared list is readable. */
  owner?: string | null;
  /** Bumped by the server on every overwrite of the same name. */
  version?: number;
  /** The workbench document this canvas was saved alongside. */
  documentId?: string | null;
  /** True while this entry exists only in localStorage and has not been migrated. */
  local?: boolean;
}

// TASK-876: read-only now. The device-local store is no longer written to; it is read
// once so workstreams saved before this change are offered for migration rather than
// silently disappearing the day the feature improved. `MAX_WORKSTREAMS` went with the
// writer — the cap belonged to a localStorage quota, and the server bounds tiles per
// workstream (MAX_SPECS) rather than workstreams per account.
const WORKSTREAMS_KEY = 'kielis-workstreams';

const readWorkstreams = (): SavedWorkstream[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(WORKSTREAMS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// TASK-876: `writeWorkstreams` is gone. Nothing writes to the device-local store any
// more — keeping a second writer would recreate the two-persistence-models problem this
// ticket exists to remove, with the added twist that the two copies could disagree.
// `readWorkstreams` above stays, read-only, so pre-existing saves are still offered.

const MAX_HISTORY = 20;

// SixEase-faithful "open a client" (ports AppShell `DEFAULT_VIEW_SPECS` + `openClient`):
// opening a client resolves to the RM's default view, NOT a hardcoded widget — so every
// client-open entry point (book row, /client, alert) lands in the same preferred view.
export type DefaultView = 'holdings' | 'dna' | 'portfolio' | 'alerts';
const DEFAULT_VIEW_SPECS: Record<DefaultView, (clientId: string) => WidgetSpec[]> = {
  holdings: (clientId) => [{ component: 'Client360', props: { clientId } }],
  dna: (clientId) => [{ component: 'DnaCard', props: { clientId } }],
  portfolio: (clientId) => [{ component: 'CockpitPortfolioView', props: { clientId } }],
  alerts: (clientId) => [{ component: 'ConflictsList', props: { clientId } }],
};

// Persisted across reloads like SixEase PrefsProvider (only this one pref survived the
// port — Kielis dropped density/actionCenterOpen). A settings toggle can call setDefaultView.
const VIEW_STORAGE_KEY = 'kielis-canvas-default-view';
const readDefaultView = (): DefaultView => {
  if (typeof window === 'undefined') return 'holdings';
  const v = window.localStorage.getItem(VIEW_STORAGE_KEY);
  return v === 'dna' || v === 'portfolio' || v === 'alerts' || v === 'holdings' ? v : 'holdings';
};

/** TASK-640: an agent-requested navigation to a workbench document. */
export interface PendingOpenDocument {
  documentId: string;
  title?: string;
  /**
   * TASK-796: the task the turn that opened this document ran under, so the
   * workbench arrives with its banner and objective instead of taskless. Absent
   * when the turn named no task — which stays the common case and must not be
   * fabricated. It is the TURN's task, not a claim that the document belongs to
   * it; the rail's origin transcript is still gated on TASK-788's verified
   * binding.
   */
  taskId?: string;
}

/**
 * TASK-796: the one mapping from an `OpenDocument` widget chunk to the
 * pending-open slot.
 *
 * There are two widget sinks — `useAgenticChat.canvasWidgetHandler` and the
 * canvas InputDock's own — and each carried its own copy of this destructuring.
 * Both dropped every prop but `documentId` and `title`, so widening one and not
 * the other would have fixed the workbench on some surfaces and not others.
 */
export const openDocumentFromWidget = (rawProps: unknown): void => {
  const props = (rawProps ?? {}) as { documentId?: string; title?: string; taskId?: string };
  if (!props.documentId) return;
  useCanvasStore.getState().requestOpenDocument({
    documentId: props.documentId,
    title: props.title,
    taskId: props.taskId,
  });
};

interface CanvasState {
  specs: CanvasTileSpec[];
  // TASK-601 staged loading: non-null while a summon's LLM round-trip is in
  // flight; Canvas renders a WidgetSkeleton with this status line.
  pendingStatus: string | null;
  setPendingStatus: (status: string | null) => void;
  // TASK-640: OpenDocument widget chunks land here; OpenDocumentNavigator
  // (mounted inside the Router) consumes them and navigates to /workbench/:id.
  pendingOpenDocument: PendingOpenDocument | null;
  requestOpenDocument: (doc: PendingOpenDocument) => void;
  clearPendingOpenDocument: () => void;
  // TASK-650: empty-canvas starters (and any future surface) hand a prompt to
  // the InputDock, which owns the submit pipeline. Mirrors pendingOpenDocument.
  // TASK-767: a bare string means "show what you send"; a {display, payload}
  // pair means the two differ (task delegation carries ids and tool names the
  // advisor never typed).
  pendingPrompt: RequestedPrompt | null;
  requestPrompt: (prompt: RequestedPrompt) => void;
  clearPendingPrompt: () => void;
  /**
   * TASK-768: how many prompt consumers are mounted. `requestPrompt` is
   * consumed ONLY by canvas/InputDock, which mounts only on /dashboard — so
   * inside the CanvasDrawer on any other route the starter chips were dead
   * controls: clicking did nothing, with no feedback. Surfaces that offer a
   * prompt check this instead of guessing from the route.
   */
  promptConsumers: number;
  registerPromptConsumer: () => () => void;
  /**
   * TASK-768: whether the off-dashboard CanvasDrawer is currently expanded.
   * The drawer is a right-anchored slide-over; the dock's TranscriptSheet gets
   * out of its way rather than being clipped mid-sentence underneath it.
   * The geometry both sides must agree on is `CANVAS_DRAWER_GEOMETRY` below.
   */
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
  addSpecs: (specs: WidgetSpec[]) => void;
  // Open a client in the RM's default view (SixEase parity). The model/UI never
  // hardcodes which client widget opens — this indirection owns that choice.
  openClient: (clientId: string) => void;
  defaultView: DefaultView;
  setDefaultView: (view: DefaultView) => void;
  toggleCollapse: (id: string) => void;
  setRail: (id: string, rail: 'left' | null) => void;
  closeTile: (id: string) => void;
  clear: () => void;
  // TASK-651 lifecycle -------------------------------------------------------
  // Session undo: snapshots taken before every add/close/clear; undo() restores
  // the most recent one.
  history: CanvasTileSpec[][];
  undo: () => void;
  // Named workstreams (server-side since TASK-876; widget specs only — see
  // SavedWorkstream). Save and delete are ASYNC because they are now network calls; the
  // list is loaded by `loadWorkstreams` rather than read synchronously at module load.
  workstreams: SavedWorkstream[];
  activeWorkstream: string | null;
  workstreamsLoaded: boolean;
  /**
   * TASK-876: the workbench document this canvas is currently docked beside, if any.
   *
   * Set by `WorkbenchRouter` on `/workbench/:docId` and cleared when it unmounts. A saved
   * workstream records it, which is what lets the bare document link find its views
   * again — the `?canvas=` blob it replaces was lost by any clean navigation.
   */
  dockedDocumentId: string | null;
  setDockedDocument: (documentId: string | null) => void;
  /**
   * TASK-876: adopt a workstream restored from somewhere other than the workstream list
   * — today, `/workbench/:docId` restoring the views saved beside a document.
   *
   * It exists because `addSpecs` alone leaves `activeWorkstream` null, so
   * `selectHasUnsavedCanvas` would call a canvas that was JUST restored from a saved
   * workstream unsaved, and the advisor would be warned about losing work that is
   * already stored. A warning that fires when nothing is at risk is how a warning stops
   * being read.
   */
  adoptWorkstream: (entry: SavedWorkstream) => void;
  loadWorkstreams: () => Promise<void>;
  saveWorkstream: (name: string) => Promise<boolean>;
  openWorkstream: (name: string) => void;
  deleteWorkstream: (name: string) => Promise<void>;
}

// TASK-575 Two-Box Today: ChangeRadar was promoted to the always-present NOTICE column, so
// the canvas now starts empty ("summon a view") instead of seeding a radar tile. Clearing or
// closing the last tile lands on the Canvas empty state rather than re-seeding.
const DEFAULT_SPECS: WidgetSpec[] = [];

export const useCanvasStore = create<CanvasState>((set) => ({
  specs: stampTiles(DEFAULT_SPECS),
  pendingStatus: null,
  setPendingStatus: (pendingStatus) => set({ pendingStatus }),
  pendingOpenDocument: null,
  requestOpenDocument: (pendingOpenDocument) => set({ pendingOpenDocument }),
  clearPendingOpenDocument: () => set({ pendingOpenDocument: null }),
  pendingPrompt: null,
  requestPrompt: (pendingPrompt) => set({ pendingPrompt }),
  clearPendingPrompt: () => set({ pendingPrompt: null }),
  drawerOpen: false,
  setDrawerOpen: (drawerOpen) => set({ drawerOpen }),
  promptConsumers: 0,
  registerPromptConsumer: () => {
    set((s) => ({ promptConsumers: s.promptConsumers + 1 }));
    return () => set((s) => ({ promptConsumers: Math.max(0, s.promptConsumers - 1) }));
  },
  addSpecs: (newSpecs) =>
    set((s) => {
      // TASK-651: same semantic identity → UPDATE the existing tile in place
      // (new props, e.g. changed filters) instead of appending a duplicate.
      const specs = [...s.specs];
      const appended: WidgetSpec[] = [];
      for (const spec of newSpecs) {
        if (spec.component === 'ChatMessage') {
          appended.push(spec);
          continue;
        }
        const idx = specs.findIndex((t) => !t.rail && tileIdentity(t) === tileIdentity(spec));
        if (idx >= 0) {
          specs[idx] = { ...specs[idx], props: spec.props, updatedAt: Date.now() };
        } else {
          appended.push(spec);
        }
      }
      return {
        specs: [...specs, ...stampTiles(appended)],
        history: [...s.history, s.specs].slice(-MAX_HISTORY),
      };
    }),
  defaultView: readDefaultView(),
  openClient: (clientId) =>
    set((s) => ({
      specs: [...s.specs, ...stampTiles(DEFAULT_VIEW_SPECS[s.defaultView](clientId))],
      history: [...s.history, s.specs].slice(-MAX_HISTORY),
    })),
  setDefaultView: (view) => {
    if (typeof window !== 'undefined') window.localStorage.setItem(VIEW_STORAGE_KEY, view);
    set({ defaultView: view });
  },
  toggleCollapse: (id) =>
    set((s) => ({
      specs: s.specs.map((t) => (t.id === id ? { ...t, collapsed: !t.collapsed } : t)),
    })),
  setRail: (id, rail) =>
    set((s) => ({
      specs: s.specs.map((t) => (t.id === id ? { ...t, rail: rail ?? undefined } : t)),
    })),
  closeTile: (id) =>
    set((s) => {
      const remaining = s.specs.filter((t) => t.id !== id);
      return {
        specs: remaining.length > 0 ? remaining : stampTiles(DEFAULT_SPECS),
        history: [...s.history, s.specs].slice(-MAX_HISTORY),
      };
    }),
  clear: () =>
    set((s) => ({
      specs: stampTiles(DEFAULT_SPECS),
      history: [...s.history, s.specs].slice(-MAX_HISTORY),
      activeWorkstream: null,
    })),
  // TASK-651 lifecycle -------------------------------------------------------
  history: [],
  undo: () =>
    set((s) => {
      if (s.history.length === 0) return s;
      const previous = s.history[s.history.length - 1];
      return { specs: previous, history: s.history.slice(0, -1) };
    }),
  // TASK-876: seeded from the legacy device-local store so a workstream saved before this
  // change is still listed (flagged `local`) and can be re-saved to the server, rather
  // than vanishing. `loadWorkstreams` replaces this with the account's real list.
  workstreams: readWorkstreams().map((w) => ({ ...w, local: true })),
  activeWorkstream: null,
  workstreamsLoaded: false,
  dockedDocumentId: null,
  setDockedDocument: (documentId) => set({ dockedDocumentId: documentId }),
  adoptWorkstream: (entry) =>
    set((s) => ({
      specs: stampTiles(entry.specs),
      history: [...s.history, s.specs].slice(-MAX_HISTORY),
      activeWorkstream: entry.name,
      // Listed too, so `selectHasUnsavedCanvas` can compare against it and the chip row
      // shows what is open even before `loadWorkstreams` has run.
      workstreams: [entry, ...s.workstreams.filter((w) => w.name !== entry.name)],
    })),

  loadWorkstreams: async () => {
    const { listWorkstreams } = await import('../services/api/workstreamApi');
    try {
      const rows = await listWorkstreams();
      const server: SavedWorkstream[] = rows.map((w) => ({
        id: w.id,
        name: w.name,
        savedAt: w.updated_at ?? w.created_at ?? new Date().toISOString(),
        clientName: w.client_name ?? undefined,
        specs: w.specs ?? [],
        owner: w.owner,
        version: w.version,
        documentId: w.document_id,
      }));
      // A legacy entry survives in the list only while no server workstream has taken its
      // name — otherwise the same name would appear twice with different contents and the
      // restore path would have to pick one.
      const names = new Set(server.map((w) => w.name));
      const leftovers = readWorkstreams()
        .filter((w) => !names.has(w.name))
        .map((w) => ({ ...w, local: true }));
      set({ workstreams: [...server, ...leftovers], workstreamsLoaded: true });
    } catch {
      // Offline or the endpoint is unavailable: keep whatever is already listed and say
      // nothing false. An empty list would read as "you have no saved work".
      set({ workstreamsLoaded: true });
    }
  },

  saveWorkstream: async (name) => {
    const trimmed = name.trim().slice(0, 60);
    if (!trimmed) return false;
    const s = useCanvasStore.getState();
    const widgets = s.specs.filter((t) => t.component !== 'ChatMessage');
    if (widgets.length === 0) return false;
    const clientName = [...widgets]
      .reverse()
      .map((t) => t.props?.clientName)
      .find((n): n is string => typeof n === 'string' && n.length > 0);
    const clientId = [...widgets]
      .reverse()
      .map((t) => t.props?.clientId)
      .find((n): n is string => typeof n === 'string' && n.length > 0);
    const specs = widgets.map(({ component, props, size }) => ({ component, props, size }));

    const { saveWorkstream: save } = await import('../services/api/workstreamApi');
    try {
      const saved = await save({
        name: trimmed,
        specs,
        client_id: clientId,
        client_name: clientName,
        // Recorded so `/workbench/{id}` can restore these views later.
        document_id: s.dockedDocumentId ?? undefined,
      });
      const entry: SavedWorkstream = {
        id: saved.id,
        name: saved.name,
        savedAt: saved.updated_at ?? new Date().toISOString(),
        clientName: saved.client_name ?? undefined,
        // The LOCAL specs, not the server's echo. Postgres `jsonb` re-orders object keys
        // by (length, bytes) — `{clientId, status}` comes back `{status, clientId}` — and
        // `selectHasUnsavedCanvas` compares serialised props, so adopting the echo made a
        // canvas read as unsaved the instant after it was successfully saved. `shape()`
        // below canonicalises key order as well, because a workstream loaded in a later
        // session comes from the server whatever this line does.
        specs,
        owner: saved.owner,
        version: saved.version,
        documentId: saved.document_id,
      };
      set((prev) => ({
        workstreams: [entry, ...prev.workstreams.filter((w) => w.name !== trimmed)],
        activeWorkstream: trimmed,
      }));
      return true;
    } catch {
      // The save did NOT happen. Returning true here would leave the advisor believing
      // their canvas was handed over when nothing was written — the same class of defect
      // TASK-869 removed from the assistant's write claims.
      return false;
    }
  },
  openWorkstream: (name) =>
    set((s) => {
      const saved = s.workstreams.find((w) => w.name === name);
      if (!saved) return s;
      return {
        specs: stampTiles(saved.specs),
        history: [...s.history, s.specs].slice(-MAX_HISTORY),
        activeWorkstream: name,
      };
    }),
  deleteWorkstream: async (name) => {
    const target = useCanvasStore.getState().workstreams.find((w) => w.name === name);
    if (!target) return;
    if (target.id) {
      const { deleteWorkstream: remove } = await import('../services/api/workstreamApi');
      try {
        await remove(target.id);
      } catch {
        // Leave it listed: a row still on the server that has vanished from the list is
        // worse than one that refused to go.
        return;
      }
    }
    // A legacy device-local entry has no server row to delete; dropping it from the list
    // is the whole of removing it.
    set((s) => ({
      workstreams: s.workstreams.filter((w) => w.name !== name),
      activeWorkstream: s.activeWorkstream === name ? null : s.activeWorkstream,
    }));
  },
}));

/**
 * TASK-876: does the canvas hold work that navigating away would actually DESTROY?
 *
 * The ticket's sixth defect is that "navigating away silently discards an unsaved
 * canvas". The obvious reading — warn whenever nothing is saved — is wrong here, and
 * review caught it: `useCanvasUrlSync` mirrors the canvas into `?canvas=`, so summoning
 * one tile and pressing F5 restores it intact. Warning there prompts the great majority
 * of advisors, who never save a workstream, on every reload of a canvas that was never at
 * risk — which is precisely how a warning stops being read, and the same reasoning that
 * keeps conversation bubbles out of this check.
 *
 * So the question is narrower and truer: is there anything here the address bar cannot
 * bring back? `encodeCanvas` carries the component plus the `URL_SAFE_PROPS` whitelist; a
 * tile holding anything else loses it on the way out. That, and an edited copy of a saved
 * workstream, is what is worth stopping for.
 */
const urlCanCarry = (spec: { props?: Record<string, unknown> }): boolean => {
  const props = spec.props ?? {};
  return Object.entries(props).every(
    ([key, value]) =>
      value === undefined ||
      value === null ||
      value === '' ||
      ((URL_SAFE_PROPS as readonly string[]).includes(key) && typeof value === 'string'),
  );
};

export const selectHasUnsavedCanvas = (s: CanvasState): boolean => {
  const widgets = s.specs.filter((t) => t.component !== 'ChatMessage');
  if (widgets.length === 0) return false;
  const active = s.workstreams.find((w) => w.name === s.activeWorkstream);
  if (!active) {
    // Never saved — at risk only where the URL cannot reconstruct the tile.
    return widgets.some((t) => !urlCanCarry(t));
  }
  // Compared on the specs themselves rather than a counter: opening a saved workstream
  // and then adding a tile is unsaved work, and a length check alone would miss a swap.
  //
  // Key order is canonicalised because a workstream that has been to the server and back
  // has been through `jsonb`, which re-orders object keys by (length, bytes). Comparing
  // raw `JSON.stringify` output would call an unchanged canvas modified purely because
  // Postgres spells the same object differently — and then warn the advisor about losing
  // work that is already saved.
  const canonical = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(canonical);
    if (value && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value as Record<string, unknown>)
          .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
          .map(([k, v]) => [k, canonical(v)]),
      );
    }
    return value;
  };
  const shape = (list: { component: string; props?: unknown }[]) =>
    JSON.stringify(list.map((t) => [t.component, canonical(t.props ?? null)]));
  return shape(widgets) !== shape(active.specs);
};

// Canvas focus (TASK-499): the client id of the most recently summoned client-scoped
// tile. The InputDock sends this as the summon's client_hint. No stored state — derived.
export const selectLastClientId = (s: CanvasState): string | undefined =>
  [...s.specs].reverse().find((t) => typeof t.props.clientId === 'string')?.props
    .clientId as string | undefined;

/**
 * TASK-707: the meeting already open on the canvas, if any.
 *
 * Sent to the UI brain as a HINT so "review this meeting" resolves to the one the RM is
 * looking at. The server validates it against the focused client's own completed meetings
 * before using it — this selector is not authority, it is context.
 */
export const selectLastSessionId = (s: CanvasState): string | undefined =>
  [...s.specs].reverse().find((t) => typeof t.props.sessionId === 'string')?.props
    .sessionId as string | undefined;

// SixEase-faithful action surface (ports CanvasActions.tsx `useCanvasActions`). Widgets
// summon via addSpecs, or open a client in the RM's default view via openClient — they
// never route. Both are stable store refs, so the returned object is cheap per render.
export const useCanvasActions = (): {
  addSpecs: CanvasState['addSpecs'];
  openClient: CanvasState['openClient'];
} => ({
  addSpecs: useCanvasStore((s) => s.addSpecs),
  openClient: useCanvasStore((s) => s.openClient),
});
